/**
 * The document's name, renamed in place.
 *
 * HERE RATHER THAN IN A DIALOG BEFORE IT EXISTS. A new ParkourAI Doc is created with the next free
 * "Document N" precisely so that pressing Create in QMS puts somebody in the editor; the name is
 * then the first thing on the page, editable, where there is finally something to name it after.
 *
 * RENAMING IS NOT SAVING. It goes to the plural endpoint, carries no body, and cannot be used to
 * overwrite text -- which is why a stale tab renaming a section cannot undo somebody's edit.
 */
import React from "react";
import { Box, IconButton, InputBase, Link, Tooltip, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";

import { makeCresAxios } from "../../util/CresAxios";
import { statusPill } from "../../util/statusPill";
import Symbol from "./Symbol";
import useAppContext from "../../util/AppContext";
import { Grey5, Grey17 } from "../../theme/styles";
import { hasPrivilege } from "../../util/SessionContext";
import CustomerSharesDialog from "./CustomerSharesDialog";

export type DocumentRecord = {
    ObjectID?: string | null;
    ObjectUSID?: string | null;
    Name?: string | null;
    DisplayRevision?: string | null;
};

const QMS_URL = process.env.REACT_APP_FOUNDATION_URL ?? "";

export default function DocumentTitle({ documentUSID, name, revisionUSID, record, status, savedAt,
                                        onRenamed }: {
    documentUSID: string;
    name: string;
    revisionUSID: string;
    /** The QMS record this is a part of. Absent only before the first response lands. */
    record?: DocumentRecord | null;
    /** DRAFT / RELEASED / ... — the REVISION's status, which is the record's and not this part's. */
    status?: string | null;
    /** When the body last reached the server, from the editor below. */
    savedAt?: number | null;
    onRenamed: (name: string) => void;
}) {
    const sessionContext = useAppContext()?.sessionContext as any;
    const navigate = useNavigate();
    const [value, setValue] = React.useState(name);
    const [problem, setProblem] = React.useState<string | null>(null);
    const [sharing, setSharing] = React.useState(false);

    React.useEffect(() => { setValue(name); }, [name]);

    const commit = async () => {
        const next = value.trim();
        // AN EMPTY NAME IS A REVERT, NOT A REFUSAL. Somebody who clears the field and tabs away
        // meant to change their mind, and an error message over a title they can see is noise.
        if (!next || next === name) { setValue(name); return; }
        try {
            const axios = makeCresAxios(sessionContext);
            await axios.put("/t11/v1/document_contents", { USID: documentUSID, Name: next });
            onRenamed(next);
            setProblem(null);
        } catch (error: any) {
            // THE SERVER'S OWN SENTENCE. A duplicate name is refused with the name in it, which is
            // more use than anything invented here.
            setProblem(error?.response?.data?.Cause || "That name could not be used.");
            setValue(name);
        }
    };

    return (
        // A COLUMN, NOT A ROW, and the record line is the reason.
        //
        // The icon and the name are one row; the line beneath -- the number, the document, its
        // state and its revision -- belongs to the whole heading rather than to the name, so it
        // runs from the left edge UNDER the icon instead of being indented past it. That also puts
        // it on the same left edge as the menu bar below, so the three lines read as one block
        // rather than a heading with a stray indented caption in the middle of it.
        <Box sx={{ mb: 1.5 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
            {/* THE WAY BACK, WHERE GOOGLE DOCS PUTS IT. Ed: "How do I get back to the Doc landing
                page once I am in the editor?" -- and the honest answer was that the "Docs" wordmark
                in the bar already did it and NOTHING SAID SO. A control that works and cannot be
                found is a control somebody does not have.

                An editor is also the one screen in this app somebody arrives at from outside --
                from a QMS record, in a new tab -- so it cannot assume there is a Back to press. */}
            <Tooltip title="All documents">
                <IconButton onClick={() => navigate("/")} aria-label="All documents"
                            sx={{ mt: 0.25, width: 36, height: 36,
                                  color: "#1498D8", "&:hover": { backgroundColor: "#E9EEF6" } }}>
                    <Symbol name="description" size={26} fill={1}/>
                </IconButton>
            </Tooltip>

            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Tooltip title="Rename">
                <InputBase value={value}
                           onChange={(e) => setValue(e.target.value)}
                           onBlur={commit}
                           onKeyDown={(e) => {
                               if (e.key === "Enter") (e.target as HTMLElement).blur();
                               if (e.key === "Escape") setValue(name);
                           }}
                           inputProps={{ "aria-label": "Document name" }}
                           sx={{ fontSize: 22, fontWeight: 500, color: Grey17, width: "100%",
                                 "& input": { p: "2px 6px", borderRadius: 1 },
                                 "& input:hover": { backgroundColor: "#F1F3F4" },
                                 "& input:focus": { backgroundColor: "#FFFFFF",
                                                    outline: "2px solid #1498D8" } }}/>
            </Tooltip>
            </Box>
        </Box>

            {problem
                ? <Typography sx={{ fontSize: 12, color: "#C0392B", px: 0 }}>{problem}</Typography>
                /* WHAT THIS IS A PART OF -- the QMS record, by the number people say out loud and
                   the name they recognise. It read "Revision b1b6c30f" until Ed asked what that
                   was: eight characters of a UUID, which identifies the row to the database and
                   nobody else. A ParkourAI Doc on its own is a page of prose; this line is the only
                   thing on screen that says which controlled document it governs.

                   THE OBJECT ID IS A LINK BACK TO QMS, because having read the appendix the next
                   question is almost always about the record -- its approvals, its effective date,
                   the other parts of it. */
                : <Typography component="div" sx={{ fontSize: 12, color: Grey5, px: 0,
                                                    display: "flex", alignItems: "center", gap: 0.75,
                                                    flexWrap: "wrap" }}>
                      {record?.ObjectID && QMS_URL
                          ? <Link href={`${QMS_URL}/documents/${revisionUSID}`} target="_blank"
                                  rel="noopener noreferrer" underline="hover"
                                  sx={{ fontWeight: 600 }}>{record.ObjectID}</Link>
                          : record?.ObjectID}
                      {record?.Name ? <span>· {record.Name}</span> : null}
                      {/* THE STATUS BELONGS ON THIS LINE, with the number and the revision it is a
                          fact ABOUT. It sat on a line of its own below the parts strip, which put
                          three unrelated things between a revision and its state.

                          BEFORE THE REVISION, NOT AFTER IT, so the line ends on the thing that
                          changes most often and reads "this document, in this state, at this
                          revision" -- which is the order somebody says it out loud.

                          PAINTED FROM THE SHARED PALETTE, not a bare MUI Chip -- a bare one makes
                          DRAFT grey, and grey in this suite means superseded or cancelled. A
                          status must not mean one colour in QMS and another in Docs; it is the
                          same revision of the same document seen from two applications. */}
                      {status ? statusPill(status) : null}
                      {record?.DisplayRevision ? <span>Rev {record.DisplayRevision}</span> : null}

                      {/* FAR RIGHT ON THE SAME LINE. A save confirmation is the one thing on this
                          screen nobody reads until they go looking for it, so it belongs where the
                          eye goes last rather than in the middle of the facts about the document. */}
                      <Box sx={{ flexGrow: 1 }}/>
                      {/* TO THE CUSTOMER PORTAL. Hidden without the privilege; t1 asks it of this record. */}
                      {record?.ObjectUSID && hasPrivilege("document:shareWithCustomer") ? (
                          <Link component="button" type="button" underline="hover" onClick={() => setSharing(true)}
                                sx={{ fontSize: 12 }}>Share with customer</Link>
                      ) : null}
                      {savedAt ? (
                          <Box component="span" sx={{ whiteSpace: "nowrap" }}>
                              Saved {new Date(savedAt).toLocaleTimeString()}
                          </Box>
                      ) : null}
                  </Typography>}

            {sharing && record?.ObjectUSID ? (
                <CustomerSharesDialog kind="Document" subjectUSID={record.ObjectUSID}
                                      subjectName={record.Name ?? name} onClose={() => setSharing(false)}/>
            ) : null}
        </Box>
    );
}